import {Container} from "pixi.js";
import {inches} from "../../const";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";
import {absorbOuterSizeDelta} from "./BlockOps";
import {LinearDimension} from "./LinearDimension";
import {TFrameBlock} from "./FrameBlockExt";
import type {FrameGridView} from "./FrameGridScene";

// the frame's overall outside-to-outside rectangle in scene coords: the root block's
// laid-out rect, as the outline sticks are inside it. null before the first layout.
// Shared by the outer dimensions here and by frame-mounted hardware crosshairs, which
// position against these four outer sides.
export function frameOuterRect(root: TFrameBlock, spec: MdFrameSpec): {x: number; y: number; width: number; height: number} | null {
    const rect = root.layoutRect;
    if (!rect) return null;
    return {...rect};
}

// gap between the outer frame edge and the overall dimension line, in scene units.
// wide enough to clear the perimeter sticks, the interior cell dimensions, and the
// door's nominal dimensions — those stack outside the frame too (DoorNominalDimensions,
// at DimensionStack's BASE_GAP), and detail dimensions belong inside the overall.
const OUTER_GAP = 13 * inches;

// the overall outside-to-outside dimensions of the whole frame — one horizontal
// (above the frame) and one vertical (left of the frame). These span the root block,
// outline sticks included, so editing one pins the root's size var (userSet ->
// isModified), which the solver then treats as a hard constraint and reflows the
// cells around — exactly like editing a cell. When unpinned, the root var is written
// back by the solver each layout, so the label auto-updates as other dimensions push
// the frame.
export class FrameOuterDimension {
    private view: FrameGridView;
    private container: Container;
    private widthDim: LinearDimension;
    private heightDim: LinearDimension;

    constructor(view: FrameGridView) {
        this.view = view;
        this.container = new Container();

        const app = view.elev.draw;
        this.widthDim = new LinearDimension(this.container, app);
        this.heightDim = new LinearDimension(this.container, app);
    }

    // draw the overall dimensions for `root` (the grid's root block). Called after
    // each layout, once root.layoutRect is known.
    draw(root: TFrameBlock, spec: MdFrameSpec) {
        // re-attach to the current lines layer every draw: a grid rebuild resets
        // parentThing.lines to a fresh Graphics, orphaning our container, and the
        // re-add also lifts the dimensions above the cell fills.
        this.view.parentThing.lines.addChild(this.container);

        // in external-dimensions mode (the screen export) the overall dimensions are
        // drawn stacked outside by ExportFrameDimensions instead — hide the inline ones.
        if(this.view.elev.viewSettings.externalDimensions.value) {
            this.hide();
            return;
        }

        const width = root.width;
        const height = root.height;
        const outer = frameOuterRect(root, spec);
        if(!outer || !width || !height) {
            this.hide();
            return;
        }

        const outerW = outer.width;
        const outerH = outer.height;

        // container at the frame's outer top-left, so the dimensions span 0..outer.
        this.container.position.set(outer.x, outer.y);

        this.widthDim.draw({
            axis: "horizontal",
            from: 0,
            to: outerW,
            cross: -OUTER_GAP,
            value: outerW,
            modified: width.isModified(),
            onSet: (v) => {
                // when every cell along the axis is pinned, hand the delta to one
                // cell explicitly — otherwise the solver has nowhere to put it.
                absorbOuterSizeDelta(spec, "width", v - root.getWidth(true));
                width.userSet(v, {debug: "outer-width-dim-edit"});
                this.relayout();
            },
            onClear: () => {
                width.userClearModified();
                this.relayout();
            },
        });

        this.heightDim.draw({
            axis: "vertical",
            from: 0,
            to: outerH,
            cross: -OUTER_GAP,
            value: outerH,
            modified: height.isModified(),
            onSet: (v) => {
                absorbOuterSizeDelta(spec, "height", v - root.getHeight(true));
                height.userSet(v, {debug: "outer-height-dim-edit"});
                this.relayout();
            },
            onClear: () => {
                height.userClearModified();
                this.relayout();
            },
        });
    }

    private relayout() {
        this.view.render();
        this.view.parentThing.changed.emit("re-layout");
    }

    hide() {
        this.widthDim.hide();
        this.heightDim.hide();
    }

    destroy() {
        this.widthDim.destroy();
        this.heightDim.destroy();
        // the container lives under parentThing.lines, so a grid/scene tree
        // destroy (children:true) may have already freed it — guard the free.
        if (!this.container.destroyed) this.container.destroy();
    }
}
